import React from 'react'
import classNames from 'classnames'
import kebabCase from 'lodash.kebabcase'
import { mq } from 'marketing-site/src/library/utils'
import { IProps } from './index'
import { HeadingBase } from 'marketing-site/src/mvp-v2/library/elements/HeadingBase'

export const FeatureSpotlightVertical: React.FC<IProps> = ({
  icon,
  eyebrow,
  title,
  titleSize = 'heading-2',
  subhead,
  ctas,
  media,
  mediaWidth = 'full-width',
  backgroundColor = 'transparent',
  titleWidth = 'default',
  theme = 'none',
  hasMediaShadow = true,
}) => {
  const classes = classNames('feature-spotlight-vertical', 'grid-layout', {
    [`mvp-background-color-${kebabCase(backgroundColor)}`]: backgroundColor,
    [`feature-spotlight-vertical--theme-${kebabCase(theme)}`]: theme !== 'none',
  })

  const mediaClasses = classNames('feature-spotlight-vertical__media', {
    [`feature-spotlight-vertical__media--${mediaWidth}`]: mediaWidth,
    [`feature-spotlight-vertical__media--has-shadow`]: hasMediaShadow,
  })

  const iconClasses = classNames('feature-spotlight-vertical__media', {
    [`feature-spotlight-vertical__media--${mediaWidth}`]: mediaWidth,
  })

  return (
    <div className={classes} data-testid="feature-spotlight-vertical">
      <div className="feature-spotlight-vertical__inner">
        {icon && <div className={iconClasses}>{icon}</div>}
        <HeadingBase
          theme={theme}
          eyebrow={eyebrow}
          title={title}
          titleSize={titleSize}
          subhead={subhead}
          width={titleWidth}
        />
        {ctas && <div className="feature-spotlight-vertical__ctas">{ctas}</div>}

        {media && <div className={mediaClasses}>{media}</div>}
      </div>

      <style jsx>
        {`
          .feature-spotlight-vertical {
            position: relative;

            &--theme-blue-gradient {
              background: linear-gradient(
                180deg,
                rgba(196, 224, 254, 0) 0%,
                rgba(196, 224, 254, 0.1) 10%,
                rgba(94, 173, 255, 0.2) 30%,
                rgba(196, 224, 254, 0.1) 50%,
                rgba(196, 224, 254, 0) 100%
              );
              color: var(--mvp-slate);
            }

            &__inner {
              align-items: center;
              position: relative;
              z-index: 10;
            }

            &__media {
              border-radius: 1rem;
              overflow: hidden;
              grid-row: 3;
              grid-column: 1/-1;
              margin-top: 2.5rem;

              &--has-shadow {
                box-shadow: 0 8px 16px 0 rgba(0, 87, 255, 0.16), 0 0 1px 0 rgba(0, 87, 255, 0.04);
              }

              & > :global(picture),
              & > :global(picture img) {
                width: 100%;
              }
            }

            & :global(.heading-base__subhead .rich-text > * + *) {
              margin-top: 1.25rem;
            }

            & :global(.heading-base__subhead .rich-text > ul li) {
              margin-bottom: 0.5rem;
            }

            & :global(.heading-base__subhead .rich-text > br:last-child) {
              display: none;
            }

            &__ctas {
              display: flex;
              flex-direction: column;
              column-gap: 1.5rem;
              grid-column: 1/-1;
              grid-row: 2;
              justify-content: center;
              margin-top: 2rem;

              & > :global(.cta-link + .cta-link) {
                margin-top: 0.5rem;
              }

              & > :global(.cta-link + [class*='cta-link--link-only']) {
                margin-top: 1.375rem;
              }

              & :global(form) {
                max-width: 27.5rem;
              }
            }
          }

          @media (${mq.tablet}) {
            .feature-spotlight-vertical {
              &--theme-blue-gradient {
                background: linear-gradient(
                  180deg,
                  rgba(196, 224, 254, 0) 0%,
                  rgba(196, 224, 254, 0.2) 10%,
                  rgba(94, 173, 255, 0.3) 20%,
                  rgba(196, 224, 254, 0.2) 50%,
                  rgba(196, 224, 254, 0) 100%
                );
              }
              & :global(.heading-base__subhead .rich-text > * + *) {
                margin-top: 1.5rem;
              }
              & :global(.heading-base__eyebrow + .heading-base__title) {
                margin-top: 1.25rem;
              }
              & :global(.heading-base__title + .heading-base__subhead) {
                margin-top: 1rem;
              }

              &__ctas {
                flex-direction: row;
                align-items: center;
                margin-top: 2.5rem;

                & > :global(.cta-link + .cta-link),
                & > :global(.cta-link + [class*='cta-link--link-only']) {
                  margin-top: 0;
                }

                & > :global(.cta-link + .cta-link--link-only-black) {
                  margin-top: 0;
                  margin-left: 0.75rem;
                }
              }

              &__media {
                border-radius: 1.5rem;
                margin-top: 3.5rem;

                &--has-shadow {
                  box-shadow: 0 12px 24px 0 rgba(0, 87, 255, 0.16),
                    0 1px 1px 0 rgba(0, 87, 255, 0.04);
                }
              }
            }
          }

          @media (${mq.desktop}) {
            .feature-spotlight-vertical {
              & :global(.heading-base__eyebrow + .heading-base__title) {
                margin-top: 1.5rem;
              }
              & :global(.heading-base__title + .heading-base__subhead) {
                margin-top: 1.5rem;
              }

              &__media {
                margin-top: 4rem;
                &--inset {
                  grid-column: 2/-2;
                }

                &--full-width {
                  grid-column: 1/-1;
                }
              }
            }
          }
        `}
      </style>
    </div>
  )
}
